import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authAPI } from '@/utils/api';
import { setToken, setUser } from '@/utils/auth';
import { toast } from 'sonner';
import { EnvelopeSimple, ArrowLeft, CheckCircle } from '@phosphor-icons/react';

/* PayGBot dark auth palette — mirrors landing + dashboard */
const AUTH_BG = '#0F1117';
const AUTH_PANEL = '#171A22';
const AUTH_CARD = '#1E222D';
const AUTH_BORDER = 'rgba(255,255,255,0.08)';

/* Consistent dark input styles across all auth screens */
const INPUT_BASE =
  'w-full px-4 py-3 rounded-lg text-white placeholder-slate-500 border transition-all ' +
  'focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500/60';

const AuthBackground = ({ children }) => (
  <div
    className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12 relative overflow-hidden"
    style={{ backgroundColor: AUTH_BG }}
    data-testid="auth-page-shell"
  >
    {/* Ambient emerald glows */}
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <div
        className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full opacity-20 blur-3xl"
        style={{ background: 'radial-gradient(closest-side, #10b981, transparent)' }}
      />
      <div
        className="absolute top-1/2 -right-40 w-[520px] h-[520px] rounded-full opacity-10 blur-3xl"
        style={{ background: 'radial-gradient(closest-side, #34d399, transparent)' }}
      />
    </div>
    <div className="relative w-full flex items-center justify-center">{children}</div>
  </div>
);

const AuthLogo = ({ testId }) => (
  <Link to="/" className="inline-flex items-center space-x-2" data-testid={testId}>
    <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-600 rounded-lg flex items-center justify-center shadow-lg shadow-emerald-500/20">
      <span className="text-white font-bold text-xl">P</span>
    </div>
    <span className="text-2xl font-bold text-white tracking-tight">PayGBot</span>
  </Link>
);

const LoginPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await authAPI.login(formData);
      const { access_token, user } = response.data;

      setToken(access_token);
      setUser(user);

      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Please enter your email address');
      return;
    }

    setForgotLoading(true);
    try {
      await authAPI.forgotPassword(forgotEmail);
      setForgotSent(true);
      toast.success('Password reset instructions sent!');
    } catch (error) {
      // Still show success to prevent email enumeration
      setForgotSent(true);
    } finally {
      setForgotLoading(false);
    }
  };

  /* -------------------- Forgot Password Screen -------------------- */
  if (showForgotPassword) {
    return (
      <AuthBackground>
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <AuthLogo testId="forgot-password-logo" />
            <h2 className="mt-6 text-3xl font-bold text-white tracking-tight">Reset Password</h2>
            <p className="mt-2 text-sm text-slate-400">
              {forgotSent
                ? 'Check your email for reset instructions'
                : 'Enter your email to receive reset instructions'}
            </p>
          </div>

          <div
            className="mt-8 p-8 rounded-2xl border shadow-2xl"
            style={{ backgroundColor: AUTH_CARD, borderColor: AUTH_BORDER }}
          >
            {forgotSent ? (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto bg-emerald-500/15 border border-emerald-500/30">
                  <CheckCircle size={32} className="text-emerald-400" weight="fill" />
                </div>
                <p className="text-slate-300 leading-relaxed">
                  If an account exists with{' '}
                  <strong className="text-white">{forgotEmail}</strong>, you will receive password
                  reset instructions.
                </p>
                <button
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotSent(false);
                    setForgotEmail('');
                  }}
                  data-testid="back-to-login-btn"
                  className="w-full py-3 px-4 bg-gradient-to-br from-emerald-500 to-green-600 hover:shadow-lg hover:shadow-emerald-500/25 text-white font-medium rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                >
                  Back to Login
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-6">
                <div>
                  <label
                    htmlFor="forgot-email"
                    className="block text-sm font-medium text-slate-200 mb-2"
                  >
                    Email address
                  </label>
                  <div className="relative">
                    <EnvelopeSimple
                      size={20}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
                    />
                    <input
                      id="forgot-email"
                      type="email"
                      required
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      data-testid="forgot-email-input"
                      className={`${INPUT_BASE} pl-10`}
                      style={{ backgroundColor: AUTH_PANEL, borderColor: AUTH_BORDER }}
                      placeholder="you@example.com"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={forgotLoading}
                  data-testid="forgot-submit-btn"
                  className="w-full py-3 px-4 bg-gradient-to-br from-emerald-500 to-green-600 hover:shadow-lg hover:shadow-emerald-500/25 text-white font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                >
                  {forgotLoading ? 'Sending...' : 'Send Reset Instructions'}
                </button>

                <button
                  type="button"
                  onClick={() => setShowForgotPassword(false)}
                  className="w-full flex items-center justify-center space-x-2 text-slate-400 hover:text-emerald-400 transition-colors focus:outline-none"
                >
                  <ArrowLeft size={18} />
                  <span>Back to login</span>
                </button>
              </form>
            )}
          </div>
        </div>
      </AuthBackground>
    );
  }

  /* -------------------- Login Screen -------------------- */
  return (
    <AuthBackground>
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <AuthLogo testId="login-logo" />
          <h2 className="mt-6 text-3xl font-bold text-white tracking-tight">Welcome back</h2>
          <p className="mt-2 text-sm text-slate-400">Sign in to your account</p>
        </div>

        <form
          className="mt-8 space-y-6 p-8 rounded-2xl border shadow-2xl"
          style={{ backgroundColor: AUTH_CARD, borderColor: AUTH_BORDER }}
          onSubmit={handleSubmit}
          data-testid="login-form"
        >
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-200 mb-2">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                data-testid="login-email-input"
                value={formData.email}
                onChange={handleChange}
                className={INPUT_BASE}
                style={{ backgroundColor: AUTH_PANEL, borderColor: AUTH_BORDER }}
                placeholder="you@example.com"
                autoComplete="email"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="password" className="block text-sm font-medium text-slate-200">
                  Password
                </label>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  data-testid="forgot-password-link"
                  className="text-sm text-emerald-400 hover:text-emerald-300 font-medium transition-colors focus:outline-none focus:underline"
                >
                  Forgot Password?
                </button>
              </div>
              <input
                id="password"
                name="password"
                type="password"
                required
                data-testid="login-password-input"
                value={formData.password}
                onChange={handleChange}
                className={INPUT_BASE}
                style={{ backgroundColor: AUTH_PANEL, borderColor: AUTH_BORDER }}
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-button"
            className="w-full py-3 px-4 bg-gradient-to-br from-emerald-500 to-green-600 hover:shadow-lg hover:shadow-emerald-500/25 text-white font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </button>

          <div className="text-center text-sm">
            <span className="text-slate-400">Don&apos;t have an account? </span>
            <Link
              to="/signup"
              data-testid="login-signup-link"
              className="font-medium text-emerald-400 hover:text-emerald-300 transition-colors focus:outline-none focus:underline"
            >
              Sign up
            </Link>
          </div>
        </form>
      </div>
    </AuthBackground>
  );
};

export default LoginPage;
